/**
 * Comment & suggestion threads over documents — pure model + (de)serialization.
 *
 * Persistence is the SIDECAR-block pattern (zero new tables / schema / Rust):
 * one thread = one `block` row on the page with `type === "comment-thread"`
 * and `block.id === thread.id`. The whole thread (messages, status, kind) is
 * JSON-serialized into `block.content`; `block.text` carries a plaintext
 * projection for search. Sidecar rows ride along with the page — sync,
 * multi-org sharing, trash cascade — but are invisible to the editor content
 * pipeline (see `documents/kinds.ts` → `SIDECAR_BLOCK_TYPES`).
 *
 * Anchoring lives in the document itself: a TipTap mark (`comment` /
 * `suggestionInsert` / `suggestionDelete`) carries the thread id, so anchors
 * move naturally with edits and sync with the content. `anchorText` here is a
 * snapshot for context (panel display + survives the mark being deleted).
 */
import type { Block } from "@folio/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { COMMENT_THREAD_BLOCK_TYPE } from "../documents/kinds";
import type { EditorBlockNode } from "../pages/mutations";

export interface CommentAuthor {
  id: string;
  name: string;
}

export interface CommentMessage {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  createdAt: string;
}

export type CommentThreadKind = "comment" | "suggestion";
export type CommentThreadStatus = "open" | "resolved";
/** What the suggestion proposes to do with the marked range. */
export type SuggestionAction = "insert" | "delete";

export interface CommentThread {
  version: 1;
  /** Thread id — equals the sidecar `block.id` AND the editor mark's thread id. */
  id: string;
  kind: CommentThreadKind;
  status: CommentThreadStatus;
  /** Snapshot of the anchored text at creation (context in the panel). */
  anchorText: string;
  /** Present iff `kind === "suggestion"`. */
  suggestion?: { action: SuggestionAction };
  messages: CommentMessage[];
  createdAt: string;
  resolvedAt: string | null;
  /** For resolved suggestions: how the review ended. */
  outcome?: "accepted" | "rejected";
}

const ANCHOR_TEXT_MAX = 240;

function clampAnchor(text: string): string {
  const trimmed = text.trim();
  return trimmed.length > ANCHOR_TEXT_MAX ? `${trimmed.slice(0, ANCHOR_TEXT_MAX)}…` : trimmed;
}

function makeMessage(author: CommentAuthor, text: string, at: string): CommentMessage {
  return {
    id: generateId(),
    authorId: author.id,
    authorName: author.name,
    text: text.trim(),
    createdAt: at,
  };
}

/** New comment thread anchored to `anchorText`, opened with a first message. */
export function createCommentThread(options: {
  author: CommentAuthor;
  text: string;
  anchorText: string;
  id?: string;
}): CommentThread {
  const at = nowISO();
  return {
    version: 1,
    id: options.id ?? generateId(),
    kind: "comment",
    status: "open",
    anchorText: clampAnchor(options.anchorText),
    messages: [makeMessage(options.author, options.text, at)],
    createdAt: at,
    resolvedAt: null,
  };
}

/**
 * New suggestion thread. `anchorText` is the proposed insertion (action
 * "insert") or the text proposed for removal (action "delete"). An optional
 * note becomes the first message of the discussion.
 */
export function createSuggestionThread(options: {
  author: CommentAuthor;
  action: SuggestionAction;
  anchorText: string;
  note?: string;
  id?: string;
}): CommentThread {
  const at = nowISO();
  const note = options.note?.trim();
  return {
    version: 1,
    id: options.id ?? generateId(),
    kind: "suggestion",
    status: "open",
    anchorText: clampAnchor(options.anchorText),
    suggestion: { action: options.action },
    messages: note ? [makeMessage(options.author, note, at)] : [],
    createdAt: at,
    resolvedAt: null,
  };
}

/** Append a reply. Immutable — returns a new thread. */
export function addThreadMessage(
  thread: CommentThread,
  author: CommentAuthor,
  text: string,
): CommentThread {
  const trimmed = text.trim();
  if (trimmed === "") {
    return thread;
  }
  return { ...thread, messages: [...thread.messages, makeMessage(author, trimmed, nowISO())] };
}

export function resolveThread(
  thread: CommentThread,
  outcome?: "accepted" | "rejected",
): CommentThread {
  if (thread.status === "resolved") {
    return thread;
  }
  return {
    ...thread,
    status: "resolved",
    resolvedAt: nowISO(),
    ...(outcome ? { outcome } : {}),
  };
}

export function reopenThread(thread: CommentThread): CommentThread {
  if (thread.status === "open") {
    return thread;
  }
  const { outcome: _outcome, ...rest } = thread;
  return { ...rest, status: "open", resolvedAt: null };
}

/** Plaintext projection persisted in `block.text` (search with LIKE). */
export function threadPlainText(thread: CommentThread): string {
  return [thread.anchorText, ...thread.messages.map((m) => `${m.authorName}: ${m.text}`)]
    .filter((line) => line.length > 0)
    .join("\n");
}

// ── (De)serialization thread ↔ sidecar block ────────────────────────────────

export function threadToBlockNode(thread: CommentThread): EditorBlockNode {
  return {
    id: thread.id,
    type: COMMENT_THREAD_BLOCK_TYPE,
    content: JSON.stringify(thread),
    text: threadPlainText(thread),
  };
}

function isMessage(value: unknown): value is CommentMessage {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const m = value as Partial<Record<keyof CommentMessage, unknown>>;
  return (
    typeof m.id === "string" &&
    typeof m.authorId === "string" &&
    typeof m.authorName === "string" &&
    typeof m.text === "string" &&
    typeof m.createdAt === "string"
  );
}

function isThread(value: unknown): value is CommentThread {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const t = value as Partial<Record<keyof CommentThread, unknown>>;
  return (
    typeof t.id === "string" &&
    (t.kind === "comment" || t.kind === "suggestion") &&
    (t.status === "open" || t.status === "resolved") &&
    typeof t.anchorText === "string" &&
    typeof t.createdAt === "string" &&
    Array.isArray(t.messages) &&
    t.messages.every((m) => isMessage(m))
  );
}

/**
 * Parses a sidecar block back into a thread. Corrupt/foreign rows yield
 * `null` (tolerated and skipped — a bad row must never break the panel).
 */
export function blockToThread(row: Pick<Block, "type" | "content">): CommentThread | null {
  if (row.type !== COMMENT_THREAD_BLOCK_TYPE || !row.content) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(row.content);
    return isThread(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * All threads of a page from its block rows: open threads first (oldest
 * first — discussion order), then resolved ones (most recently resolved
 * first).
 */
export function blocksToThreads(rows: Pick<Block, "type" | "content">[]): CommentThread[] {
  const threads = rows
    .map((row) => blockToThread(row))
    .filter((thread): thread is CommentThread => thread !== null);
  const open = threads
    .filter((t) => t.status === "open")
    .toSorted((a, b) => a.createdAt.localeCompare(b.createdAt));
  const resolved = threads
    .filter((t) => t.status === "resolved")
    .toSorted((a, b) => (b.resolvedAt ?? "").localeCompare(a.resolvedAt ?? ""));
  return [...open, ...resolved];
}
